import { useEffect } from 'react';
import { createFileRoute, Link, useSearch } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle, Calendar, ArrowRight, Package } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Separator } from '@/components/ui/Separator';
import { Spinner } from '@/components/ui/Spinner';
import { formatCurrency, formatDate } from '@/lib/utils';
import { portalAPI } from '@/lib/api/portal';
import { supabase } from '@/lib/api/supabase';
import { siteConfig } from '@/config/site.config';
import { clearCart } from '@/lib/utils/cartPersistence';
import { useAuth } from '@/hooks/useAuth';

interface SuccessPageSearch {
  session_id?: string;
  order_id?: string;
}

export const Route = createFileRoute('/success')({
  component: SuccessPage,
  validateSearch: (search: Record<string, unknown>): SuccessPageSearch => {
    return {
      session_id: search.session_id as string | undefined,
      order_id: search.order_id as string | undefined,
    };
  },
});

function SuccessPage() {
  const search = useSearch({ from: '/success' });
  const { user } = useAuth();

  // Clear cart on successful payment
  useEffect(() => {
    if (search.session_id || search.order_id) {
      clearCart(user?.id);
    }
  }, [search.session_id, search.order_id, user?.id]);

  // Fetch event for display
  const { data: event } = useQuery({
    queryKey: ['event'],
    queryFn: () => portalAPI.getEvent(),
  });

  // Fetch order by session_id or order_id
  const { data: order, isLoading: isOrderLoading } = useQuery({
    queryKey: ['order', search.session_id, search.order_id],
    queryFn: async () => {
      let query = supabase
        .from('orders')
        .select('*, order_items(*)')
        .eq('event_id', siteConfig.eventId);

      if (search.session_id) {
        query = query.eq('stripe_session_id', search.session_id);
      } else if (search.order_id) {
        query = query.eq('id', search.order_id);
      }

      const { data, error } = await query.single();
      if (error) throw error;
      return data;
    },
    enabled: !!(search.session_id || search.order_id),
    retry: 3,
    retryDelay: 1000,
  });

  if (!search.session_id && !search.order_id) {
    return (
      <div className="container py-12">
        <Card className="max-w-lg mx-auto text-center">
          <CardHeader>
            <CardTitle>No Order Found</CardTitle>
            <CardDescription>
              We couldn't find your order. If you just completed a purchase, please check your email
              for confirmation.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/">
              <Button>Return Home</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container py-12 space-y-8">
      {/* Success Banner */}
      <div className="text-center space-y-4">
        <div className="flex justify-center">
          <div className="rounded-full bg-green-100 p-4">
            <CheckCircle className="h-16 w-16 text-green-600" />
          </div>
        </div>
        <h1 className="text-3xl font-heading font-bold">Registration Complete!</h1>
        <p className="text-muted-foreground max-w-md mx-auto">
          Thank you for registering. A confirmation email has been sent to your email address.
        </p>
      </div>

      <Separator />

      {/* Order Details */}
      {isOrderLoading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : order ? (
        <Card className="max-w-2xl mx-auto">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Order Details
            </CardTitle>
            <CardDescription>
              Order #{order.order_number || order.id.slice(0, 8).toUpperCase()}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {order.order_items?.map((item: any) => (
              <div key={item.id} className="flex justify-between py-2 border-b last:border-0">
                <div>
                  <p className="font-medium">{item.item_name}</p>
                  {item.quantity > 1 && (
                    <p className="text-sm text-muted-foreground">
                      Qty: {item.quantity}
                    </p>
                  )}
                  {item.variant_name && (
                    <p className="text-sm text-muted-foreground">{item.variant_name}</p>
                  )}
                </div>
                <p className="font-medium">{formatCurrency(item.total)}</p>
              </div>
            ))}

            <div className="pt-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotal</span>
                <span>{formatCurrency(order.subtotal)}</span>
              </div>
              {order.discount_amount > 0 && (
                <div className="flex justify-between text-sm text-green-600">
                  <span>Discount {order.discount_code && `(${order.discount_code})`}</span>
                  <span>-{formatCurrency(order.discount_amount)}</span>
                </div>
              )}
              {order.tax > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Tax</span>
                  <span>{formatCurrency(order.tax)}</span>
                </div>
              )}
              <div className="flex justify-between text-lg font-bold pt-2 border-t">
                <span>Total</span>
                <span>{formatCurrency(order.total)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {/* What's Next */}
      <Card className="max-w-2xl mx-auto">
        <CardHeader>
          <CardTitle>What's Next?</CardTitle>
          <CardDescription>
            Here's what you need to know before the event
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Event Details */}
          {event && (
            <div className="flex items-start gap-4 p-4 bg-muted/50 rounded-lg">
              <Calendar className="h-6 w-6 text-primary mt-1" />
              <div>
                <h3 className="font-medium">{event.name}</h3>
                <p className="text-sm text-muted-foreground">
                  {formatDate(event.startDate)}
                  {event.endDate !== event.startDate && ` - ${formatDate(event.endDate)}`}
                </p>
                {event.venue?.name && (
                  <p className="text-sm text-muted-foreground">{event.venue.name}</p>
                )}
              </div>
            </div>
          )}

          {/* Next Steps */}
          <div className="space-y-4">
            <h3 className="font-medium">Before the Event:</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <div className="rounded-full bg-primary/10 p-1.5 mt-0.5">
                  <span className="text-xs font-bold text-primary">1</span>
                </div>
                <div>
                  <p className="font-medium">Complete Your Waiver</p>
                  <p className="text-sm text-muted-foreground">
                    Check your email for the waiver link and sign it before arriving
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="rounded-full bg-primary/10 p-1.5 mt-0.5">
                  <span className="text-xs font-bold text-primary">2</span>
                </div>
                <div>
                  <p className="font-medium">Review Tournament Rules</p>
                  <p className="text-sm text-muted-foreground">
                    Familiarize yourself with the rules for any tournaments you've entered
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="rounded-full bg-primary/10 p-1.5 mt-0.5">
                  <span className="text-xs font-bold text-primary">3</span>
                </div>
                <div>
                  <p className="font-medium">Check the Schedule</p>
                  <p className="text-sm text-muted-foreground">
                    The full event schedule will be sent closer to the event date
                  </p>
                </div>
              </li>
            </ul>
          </div>

          <Separator />

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/account" className="flex-1">
              <Button variant="outline" className="w-full">
                View My Registrations
              </Button>
            </Link>
            <Link to="/" className="flex-1">
              <Button className="w-full">
                Return Home
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>

      {/* Support Info */}
      <p className="text-center text-sm text-muted-foreground">
        Questions? Contact us at{' '}
        <a href="mailto:support@tourney.dev" className="text-primary hover:underline">
          support@tourney.dev
        </a>
      </p>
    </div>
  );
}
